import React, { useRef, useState } from "react";
import { uploadEventCoverImage } from "../../api/upload";
import { updateEvent } from "../../api/events";
import ErrorAlert from "../ErrorAlert/ErrorAlert";
import { getUserFacingErrorMessage } from "../../utils/userFacingError";

interface Props {
  value: string;
  onChange: (url: string) => void;
  eventId?: number;
  eventSlug?: string;
  required?: boolean;
}

export default function EventCoverImageField({
  value,
  onChange,
  eventId,
  eventSlug,
  required = false,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewKey, setPreviewKey] = useState(0);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setIsUploading(true);
    setUploadError(null);
    try {
      const imageUrl = await uploadEventCoverImage(file, { eventId, eventSlug });
      onChange(imageUrl);
      setPreviewKey((k) => k + 1);

      if (eventId) {
        await updateEvent(eventId, { coverImageUrl: imageUrl });
      }
    } catch (err) {
      setUploadError(
        getUserFacingErrorMessage(err, "Could not upload image."),
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = async () => {
    if (required) return;
    onChange("");
    setPreviewKey((k) => k + 1);
    if (eventId) {
      try {
        await updateEvent(eventId, { coverImageUrl: null });
      } catch (err) {
        setUploadError(
          getUserFacingErrorMessage(err, "Could not remove image."),
        );
      }
    }
  };

  return (
    <div className="text-sm">
      <span className="font-medium block mb-1">
        Cover image{required ? " *" : ""}
      </span>
      <p className="text-slate-600 text-xs mb-3">
        JPEG, PNG, WebP, GIF, or AVIF. Max 5 MB. Replacing the image uploads a
        new file with a unique name.
      </p>

      {value && (
        <div className="mb-3 border border-slate-200 rounded-sm overflow-hidden bg-slate-50 max-w-md">
          <img
            key={`${previewKey}-${value}`}
            src={value}
            alt="Event cover preview"
            className="w-full h-40 object-cover"
          />
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-2">
        <button
          type="button"
          className="eh-btn-secondary text-sm"
          disabled={isUploading}
          onClick={() => inputRef.current?.click()}
        >
          {isUploading
            ? "Uploading…"
            : value
              ? "Change image"
              : "Upload image"}
        </button>
        {value && !required && (
          <button
            type="button"
            className="eh-btn-secondary text-sm"
            disabled={isUploading}
            onClick={() => void handleRemove()}
          >
            Remove
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
        className="hidden"
        aria-label="Upload cover image"
        onChange={handleFileChange}
      />

      {uploadError && (
        <ErrorAlert inline className="mt-1">
          {uploadError}
        </ErrorAlert>
      )}

      {value && (
        <p className="text-xs text-slate-500 mt-2 break-all">{value}</p>
      )}
    </div>
  );
}
